import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

interface Kullanici { kullaniciID: number; adSoyad: string; kullaniciRolu: string; }
interface DurumIstatistik { durum: string | number; sayi: number; }
interface PersonelSatir {
  kullaniciID: number; adSoyad: string; kullaniciRolu: string;
  musteriEkleme: number; notEkleme: number; sozlesmeSayisi: number;
  atananLeadSayisi: number; leadAramaSayisi: number; leadAramaTamamlanan: number;
  kiralıkSozlesmeSayisi: number; projeSunumSayisi: number;
  projeSunumDurumlar: DurumIstatistik[]; emlakSunumSayisi: number;
  emlakSunumDurumlar: DurumIstatistik[]; leadDurumIstatistikleri: DurumIstatistik[];
}
interface RaporData { baslangic: string; bitis: string; personeller: PersonelSatir[]; }

interface ZamanKullanici {
  kullaniciID: number; adSoyad: string;
  musteriEkleme: number[]; sozlesmeEkleme: number[]; kiraEkleme: number[];
  leadArama: number[]; notEkleme: number[]; projeSunum: number[];
}
interface ZamanSerisi { gunler: string[]; kullanicilar: ZamanKullanici[]; }

type DetayTip = 'musteri' | 'not' | 'sozlesme' | 'kira' | 'leadArama' | 'projeSunum' | 'emlakSunum' | 'lead';
interface DetayModal { kullaniciID: number; adSoyad: string; tip: DetayTip; baslik: string; }

const fmt = (n: number) => n.toLocaleString('tr-TR');
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';
const fmtPara = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 0 }) + ' ₺';

const DurumChip: React.FC<{ durum: string | number; sayi: number }> = ({ durum, sayi }) => {
  const d = String(durum);
  const renkMap: Record<string, { bg: string; color: string }> = {
    'Taslak': { bg: '#f1f5f9', color: '#64748b' }, 'Gönderilen': { bg: '#dbeafe', color: '#1d4ed8' },
    'Görüntülendi': { bg: '#e0e7ff', color: '#4338ca' }, 'Onaylandı': { bg: '#dcfce7', color: '#15803d' },
    'Reddedildi': { bg: '#fee2e2', color: '#b91c1c' }, 'Tamamlandı': { bg: '#dcfce7', color: '#15803d' },
  };
  const stil = renkMap[d] || { bg: '#f1f5f9', color: '#475569' };
  return <span style={{ ...stil, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginRight: 4, display: 'inline-block' }}>{d}: {sayi}</span>;
};

/* ── Tıklanabilir Metrik Kutu ── */
const MetrikKutu: React.FC<{
  label: string; value: number; color?: string; bg?: string; sub?: string;
  onClick?: () => void; disabled?: boolean;
}> = ({ label, value, color = '#1e293b', bg = '#f8fafc', sub, onClick, disabled }) => (
  <div
    onClick={value > 0 && !disabled ? onClick : undefined}
    style={{
      background: bg, border: `1px solid ${value > 0 && !disabled ? color + '44' : '#e2e8f0'}`,
      borderRadius: 10, padding: '10px 14px', textAlign: 'center',
      cursor: value > 0 && !disabled ? 'pointer' : 'default',
      transition: 'all 0.15s',
      boxShadow: value > 0 && !disabled ? `0 0 0 0 ${color}33` : 'none',
    }}
    onMouseEnter={e => { if (value > 0 && !disabled) (e.currentTarget as HTMLDivElement).style.boxShadow = `0 2px 8px ${color}33`; }}
    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
  >
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{fmt(value)}</div>
    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontWeight: 500 }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    {value > 0 && !disabled && <div style={{ fontSize: 9, color: color, marginTop: 3, fontWeight: 600 }}>Detay →</div>}
  </div>
);

/* ── Detay Modal ── */
const DetayModalComp: React.FC<{
  modal: DetayModal; baslangic: string; bitis: string; onKapat: () => void;
}> = ({ modal, baslangic, bitis, onKapat }) => {
  const { t } = useTranslation();
  const [satirlar, setSatirlar] = useState<any[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    setYukleniyor(true);
    axiosInstance.get('/api/PersonelPerformans/detay', {
      params: { kullaniciID: modal.kullaniciID, tip: modal.tip, baslangic, bitis }
    }).then((r: any) => setSatirlar(r.data)).catch(() => {}).finally(() => setYukleniyor(false));
  }, [modal, baslangic, bitis]);

  const sutunlar: Record<DetayTip, { key: string; label: string; render?: (v: any, row: any) => React.ReactNode }[]> = {
    musteri: [
      { key: 'takipNo', label: 'Takip No' },
      { key: 'adiSoyadi', label: 'Ad Soyad' },
      { key: 'turu', label: t('label.tur') },
      { key: 'telefon1', label: 'Telefon' },
      { key: 'eklenmeTarihi', label: 'Eklenme', render: v => fmtDate(v) },
    ],
    not: [
      { key: 'takipNo', label: 'Takip No' },
      { key: 'aciklama', label: 'Not', render: v => <span style={{ fontSize: 12 }}>{v}</span> },
      { key: 'eklenmeTarihi', label: 'Tarih', render: v => fmtDate(v) },
    ],
    sozlesme: [
      { key: 'sozlesmeTakipNo', label: 'Takip No' },
      { key: 'cariAd', label: t('label.musteri') },
      { key: 'sozlesmeFiyati', label: 'Fiyat', render: v => fmtPara(v) },
      { key: 'sozlesmeTarihi', label: t('sozlesme.sozlesmeTarihi'), render: v => fmtDate(v) },
      { key: 'sozlesmeDurumu', label: 'Durum' },
    ],
    kira: [
      { key: 'takipNo', label: 'Takip No' },
      { key: 'cariAd', label: t('kira.kiraci') },
      { key: 'gayrimenkul', label: 'Gayrimenkul' },
      { key: 'kiraBedeli', label: 'Kira Bedeli', render: v => fmtPara(v) },
      { key: 'baslangicTarihi', label: t('kira.baslangic'), render: v => fmtDate(v) },
      { key: 'bitisTarihi', label: t('kira.bitis'), render: v => fmtDate(v) },
    ],
    leadArama: [
      { key: 'leadAd', label: 'Lead' },
      { key: 'note', label: 'Not', render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
      { key: 'activityDate', label: 'Tarih', render: v => fmtDate(v) },
      { key: 'nextCallDate', label: 'Sonraki Arama', render: v => v ? fmtDate(v) : '—' },
      { key: 'isCompleted', label: 'Durum', render: v => v
        ? <span style={{ color: '#15803d', fontWeight: 600, fontSize: 11 }}>{t('durum.tamamlandi')}</span>
        : <span style={{ color: '#b45309', fontSize: 11 }}>{t('durum.bekliyor')}</span> },
    ],
    projeSunum: [
      { key: 'takipNo', label: 'Takip No' },
      { key: 'baslik', label: t('label.baslik') },
      { key: 'cariAd', label: t('label.musteri') },
      { key: 'durum', label: 'Durum' },
      { key: 'olusturmaTarihi', label: 'Tarih', render: v => fmtDate(v) },
    ],
    emlakSunum: [
      { key: 'takipNo', label: 'Takip No' },
      { key: 'baslik', label: t('label.baslik') },
      { key: 'cariAd', label: t('label.musteri') },
      { key: 'durum', label: 'Durum' },
      { key: 'olusturmaTarihi', label: 'Tarih', render: v => fmtDate(v) },
    ],
    lead: [
      { key: 'fullName', label: 'Ad Soyad' },
      { key: 'phone', label: 'Telefon' },
      { key: 'email', label: 'E-posta' },
      { key: 'source', label: 'Kaynak' },
      { key: 'status', label: 'Durum' },
    ],
  };

  const cols = sutunlar[modal.tip];

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onKapat(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 820, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {/* Başlık */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{modal.baslik}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{modal.adSoyad}</div>
          </div>
          <button onClick={onKapat} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', lineHeight: 1, padding: 4 }}>×</button>
        </div>

        {/* İçerik */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {yukleniyor ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
            </div>
          ) : satirlar.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
              <p style={{ margin: 0 }}>{t('rapor.donemKayitYok', 'Bu dönemde kayıt bulunamadı.')}</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>#</th>
                  {cols.map(c => (
                    <th key={c.key} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {satirlar.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '9px 16px', color: '#94a3b8', fontSize: 12 }}>{i + 1}</td>
                    {cols.map(c => (
                      <td key={c.key} style={{ padding: '9px 16px', color: '#374151', verticalAlign: 'middle' }}>
                        {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{satirlar.length} kayıt</span>
          <button onClick={onKapat} style={{ padding: '7px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('btn.kapat')}</button>
        </div>
      </div>
    </div>
  );
};

const PERSONEL_RENKLER = [
  '#7c3aed','#0369a1','#15803d','#b45309','#b91c1c','#0f766e','#be185d','#1d4ed8','#7e22ce','#065f46',
];

const METRIK_RENKLER: Record<string, { bg: string; border: string; label: string }> = {
  musteriEkleme:  { bg: 'rgba(124,58,237,0.15)', border: '#7c3aed', label: 'Müşteri' },
  sozlesmeEkleme: { bg: 'rgba(21,128,61,0.15)',  border: '#15803d', label: 'Sözleşme' },
  kiraEkleme:     { bg: 'rgba(180,83,9,0.15)',   border: '#b45309', label: 'Kiralık' },
  leadArama:      { bg: 'rgba(29,78,216,0.15)',  border: '#1d4ed8', label: 'Lead Arama' },
  notEkleme:      { bg: 'rgba(3,105,161,0.15)',  border: '#0369a1', label: 'Not' },
  projeSunum:     { bg: 'rgba(15,118,110,0.15)', border: '#0f766e', label: 'Proje Sunum' },
};

/* ══ Ana Bileşen ══ */
const PersonelPerformansRaporu: React.FC = () => {
  const { t } = useTranslation();
  const bugunYil = new Date().getFullYear();
  const bugunAy = String(new Date().getMonth() + 1).padStart(2, '0');
  const bugunGun = String(new Date().getDate()).padStart(2, '0');

  const [baslangic, setBaslangic] = useState(`${bugunYil}-${bugunAy}-01`);
  const [bitis, setBitis] = useState(`${bugunYil}-${bugunAy}-${bugunGun}`);
  const [seciliKullanici, setSeciliKullanici] = useState<number | ''>('');
  const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
  const [rapor, setRapor] = useState<RaporData | null>(null);
  const [loading, setLoading] = useState(false);
  const [genisletilmis, setGenisletilmis] = useState<Set<number>>(new Set());
  const [detayModal, setDetayModal] = useState<DetayModal | null>(null);
  const [zamanSerisi, setZamanSerisi] = useState<ZamanSerisi | null>(null);
  const [aktifMetrik, setAktifMetrik] = useState<keyof typeof METRIK_RENKLER>('musteriEkleme');
  const [grafikMod, setGrafikMod] = useState<'toplam' | 'gunluk'>('gunluk');

  useEffect(() => {
    axiosInstance.get('/api/PersonelPerformans/kullanicilar')
      .then((r: any) => setKullanicilar(r.data)).catch(() => {});
  }, []);

  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { baslangic, bitis };
      if (seciliKullanici) params.kullaniciID = String(seciliKullanici);
      const [raporRes, zamanRes] = await Promise.all([
        axiosInstance.get('/api/PersonelPerformans', { params }),
        axiosInstance.get('/api/PersonelPerformans/zaman-serisi', { params: { baslangic, bitis } }),
      ]);
      setRapor(raporRes.data);
      setZamanSerisi(zamanRes.data);
    } catch {
    } finally { setLoading(false); }
  }, [baslangic, bitis, seciliKullanici]);

  const toggleGenislet = (id: number) => {
    setGenisletilmis(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const acDetay = (p: PersonelSatir, tip: DetayTip, baslik: string) => {
    setDetayModal({ kullaniciID: p.kullaniciID, adSoyad: p.adSoyad, tip, baslik });
  };

  const toplam = rapor?.personeller.reduce((acc, p) => ({
    musteri: acc.musteri + p.musteriEkleme, not: acc.not + p.notEkleme,
    sozlesme: acc.sozlesme + p.sozlesmeSayisi, lead: acc.lead + p.leadAramaSayisi,
    projeSunum: acc.projeSunum + p.projeSunumSayisi, emlakSunum: acc.emlakSunum + p.emlakSunumSayisi,
    kira: acc.kira + p.kiralıkSozlesmeSayisi,
  }), { musteri: 0, not: 0, sozlesme: 0, lead: 0, projeSunum: 0, emlakSunum: 0, kira: 0 });

  return (
    <div style={{ padding: 24, fontFamily: "'Inter','Roboto',sans-serif", color: '#1f2937', maxWidth: 1200, margin: '0 auto' }}>
      {/* Detay Modal */}
      {detayModal && (
        <DetayModalComp modal={detayModal} baslangic={baslangic} bitis={bitis} onKapat={() => setDetayModal(null)} />
      )}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#111827' }}>{t('personel.baslik')}</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{t('personel.aciklama', 'Personel bazlı müşteri, sözleşme, sunum ve lead istatistikleri — sayılara tıklayarak detayları görüntüleyin')}</p>
      </div>

      {/* Filtreler */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{t('label.baslangic', 'Başlangıç')}</label>
          <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#374151', outline: 'none' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{t('label.bitis', 'Bitiş')}</label>
          <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#374151', outline: 'none' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{t('label.personel', 'Personel')}</label>
          <select value={seciliKullanici} onChange={e => setSeciliKullanici(e.target.value ? Number(e.target.value) : '')}
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#374151', outline: 'none', minWidth: 180 }}>
            <option value="">{t('label.tumPersonel', 'Tüm Personel')}</option>
            {kullanicilar.map(k => <option key={k.kullaniciID} value={k.kullaniciID}>{k.adSoyad}</option>)}
          </select>
        </div>
        <button onClick={yukle} disabled={loading}
          style={{ padding: '8px 24px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? t('msg.yukleniyor', 'Yükleniyor…') : t('rapor.raporuGetir', 'Raporu Getir')}
        </button>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: t('rapor.buAy', 'Bu Ay'), fn: () => { setBaslangic(`${bugunYil}-${bugunAy}-01`); setBitis(`${bugunYil}-${bugunAy}-${bugunGun}`); } },
            { label: t('rapor.gecenAy', 'Geçen Ay'), fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 1); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const son = new Date(y, d.getMonth() + 1, 0).getDate(); setBaslangic(`${y}-${m}-01`); setBitis(`${y}-${m}-${son}`); } },
            { label: t('rapor.buYil', 'Bu Yıl'), fn: () => { setBaslangic(`${bugunYil}-01-01`); setBitis(`${bugunYil}-${bugunAy}-${bugunGun}`); } },
          ].map(btn => (
            <button key={btn.label} onClick={btn.fn}
              style={{ padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Özet Toplamlar — tıklanamaz (tüm personel toplam) */}
      {rapor && toplam && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          <MetrikKutu label={t('personel.musteriEkleme', 'Müşteri Ekleme')} value={toplam.musteri} color="#7c3aed" bg="#fdf4ff" disabled />
          <MetrikKutu label={t('personel.notEkleme', 'Not Ekleme')} value={toplam.not} color="#0369a1" bg="#f0f9ff" disabled />
          <MetrikKutu label={t('personel.satisSozlesmesi', 'Satış Sözleşmesi')} value={toplam.sozlesme} color="#15803d" bg="#f0fdf4" disabled />
          <MetrikKutu label={t('personel.kiralikSozlesme', 'Kiralık Sözleşme')} value={toplam.kira} color="#b45309" bg="#fffbeb" disabled />
          <MetrikKutu label={t('personel.leadAramasi', 'Lead Araması')} value={toplam.lead} color="#1d4ed8" bg="#eff6ff" disabled />
          <MetrikKutu label={t('personel.projeSunumu', 'Proje Sunumu')} value={toplam.projeSunum} color="#0f766e" bg="#f0fdfa" disabled />
          <MetrikKutu label={t('personel.emlakSunumu', 'Emlak Sunumu')} value={toplam.emlakSunum} color="#b91c1c" bg="#fef2f2" disabled />
        </div>
      )}

      {rapor && rapor.personeller.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
          <i className="fas fa-chart-bar" style={{ fontSize: 36, marginBottom: 12, display: 'block' }}></i>
          <p style={{ margin: 0, fontWeight: 500 }}>{t('rapor.donemKayitYok', 'Bu dönemde kayıt bulunamadı.')}</p>
        </div>
      )}

      {rapor && rapor.personeller.map(p => {
        const acik = genisletilmis.has(p.kullaniciID);
        const leadTamamlanmaOrani = p.leadAramaSayisi > 0 ? Math.round((p.leadAramaTamamlanan / p.leadAramaSayisi) * 100) : 0;
        const tumSifir = p.musteriEkleme === 0 && p.notEkleme === 0 && p.sozlesmeSayisi === 0 && p.leadAramaSayisi === 0 && p.projeSunumSayisi === 0 && p.emlakSunumSayisi === 0 && p.kiralıkSozlesmeSayisi === 0;

        return (
          <div key={p.kullaniciID} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, marginBottom: 14, overflow: 'hidden', opacity: tumSifir ? 0.6 : 1 }}>
            <div onClick={() => toggleGenislet(p.kullaniciID)}
              style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, background: acik ? '#fafafa' : '#fff', borderBottom: acik ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: '#7c3aed', flexShrink: 0 }}>
                {p.adSoyad.split(' ').map((s: string) => s[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{p.adSoyad}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{p.kullaniciRolu}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {p.musteriEkleme > 0 && <span style={{ background: '#fdf4ff', color: '#7c3aed', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{p.musteriEkleme} müşteri</span>}
                {p.sozlesmeSayisi > 0 && <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{p.sozlesmeSayisi} sözleşme</span>}
                {p.leadAramaSayisi > 0 && <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{p.leadAramaSayisi} arama</span>}
                {p.projeSunumSayisi > 0 && <span style={{ background: '#f0fdfa', color: '#0f766e', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{p.projeSunumSayisi} sunum</span>}
                {tumSifir && <span style={{ background: '#f1f5f9', color: '#94a3b8', borderRadius: 5, padding: '2px 8px', fontSize: 11 }}>{t('personel.donemIslemYok', 'Bu dönemde işlem yok')}</span>}
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: acik ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>

            {acik && (
              <div style={{ padding: '20px 20px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 }}>
                  <MetrikKutu label={t('personel.musteriEkleme', 'Müşteri Ekleme')} value={p.musteriEkleme} color="#7c3aed" bg="#fdf4ff" onClick={() => acDetay(p, 'musteri', t('personel.eklenenMusteriler', 'Eklenen Müşteriler'))} />
                  <MetrikKutu label={t('personel.notEkleme', 'Not Ekleme')} value={p.notEkleme} color="#0369a1" bg="#f0f9ff" onClick={() => acDetay(p, 'not', t('personel.eklenenNotlar', 'Eklenen Notlar'))} />
                  <MetrikKutu label={t('personel.satisSozlesmesi', 'Satış Sözleşmesi')} value={p.sozlesmeSayisi} color="#15803d" bg="#f0fdf4" onClick={() => acDetay(p, 'sozlesme', t('personel.satisSozlesmeleri', 'Satış Sözleşmeleri'))} />
                  <MetrikKutu label={t('personel.kiralikSozlesme', 'Kiralık Sözleşme')} value={p.kiralıkSozlesmeSayisi} color="#b45309" bg="#fffbeb" onClick={() => acDetay(p, 'kira', t('personel.kiralikSozlesmeler', 'Kiralık Sözleşmeler'))} />
                  <MetrikKutu label={t('personel.atananLead', 'Atanan Lead')} value={p.atananLeadSayisi} color="#6b21a8" bg="#f5f3ff" onClick={() => acDetay(p, 'lead', t('personel.atananLeadler', 'Atanan Leadler'))} />
                  <MetrikKutu label={t('personel.toplamArama', 'Toplam Arama')} value={p.leadAramaSayisi} color="#1d4ed8" bg="#eff6ff"
                    sub={p.leadAramaSayisi > 0 ? `%${leadTamamlanmaOrani} ${t('personel.tamamlandi', 'tamamlandı')}` : undefined}
                    onClick={() => acDetay(p, 'leadArama', t('personel.leadAramalari', 'Lead Aramaları'))} />
                  <MetrikKutu label={t('personel.projeSunumu', 'Proje Sunumu')} value={p.projeSunumSayisi} color="#0f766e" bg="#f0fdfa" onClick={() => acDetay(p, 'projeSunum', t('personel.projeSunumlari', 'Proje Sunumları'))} />
                  <MetrikKutu label={t('personel.emlakSunumu', 'Emlak Sunumu')} value={p.emlakSunumSayisi} color="#b91c1c" bg="#fef2f2" onClick={() => acDetay(p, 'emlakSunum', t('personel.emlakSunumlari', 'Emlak Sunumları'))} />
                </div>

                {p.leadAramaSayisi > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                      Lead Arama Tamamlanma Oranı — {p.leadAramaTamamlanan}/{p.leadAramaSayisi}
                    </div>
                    <div style={{ background: '#e5e7eb', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${leadTamamlanmaOrani}%`, background: '#16a34a', height: '100%', borderRadius: 999, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                )}

                {p.projeSunumDurumlar.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Proje Sunum Durumları</div>
                    <div>{p.projeSunumDurumlar.map(d => <DurumChip key={String(d.durum)} durum={d.durum} sayi={d.sayi} />)}</div>
                  </div>
                )}

                {p.emlakSunumDurumlar.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Emlak Sunum Durumları</div>
                    <div>{p.emlakSunumDurumlar.map(d => <DurumChip key={String(d.durum)} durum={d.durum} sayi={d.sayi} />)}</div>
                  </div>
                )}

                {p.leadDurumIstatistikleri.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Lead Durum Dağılımı (tüm atanan leadler)</div>
                    <div>{p.leadDurumIstatistikleri.map(d => <DurumChip key={String(d.durum)} durum={`Durum ${d.durum}`} sayi={d.sayi} />)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!rapor && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
          <i className="fas fa-users" style={{ fontSize: 40, marginBottom: 14, display: 'block', color: '#e9d5ff' }}></i>
          <p style={{ margin: 0, fontWeight: 500 }}>{t('rapor.tarihSecHint', 'Tarih aralığı seçin ve "Raporu Getir" butonuna tıklayın.')}</p>
        </div>
      )}

      {/* ══ GRAFİK BÖLÜMÜ ══ */}
      {zamanSerisi && rapor && (() => {
        const aktifKullanicilar = zamanSerisi.kullanicilar.filter(k => {
          const p = rapor.personeller.find(p => p.kullaniciID === k.kullaniciID);
          return p && (p.musteriEkleme + p.notEkleme + p.sozlesmeSayisi + p.leadAramaSayisi + p.projeSunumSayisi + p.emlakSunumSayisi + p.kiralıkSozlesmeSayisi) > 0;
        });

        // Günlük zaman serisi — aktif metrik için tüm personel
        const zamanGunler = zamanSerisi.gunler.map(g => {
          const d = new Date(g);
          return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
        });

        const timelineData = {
          labels: zamanGunler,
          datasets: aktifKullanicilar.map((k, i) => ({
            label: k.adSoyad,
            data: k[aktifMetrik as keyof ZamanKullanici] as number[],
            borderColor: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length],
            backgroundColor: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length] + '22',
            fill: false,
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
          })),
        };

        // Kümülatif (toplam) veri
        const kumulatifData = {
          labels: zamanGunler,
          datasets: aktifKullanicilar.map((k, i) => {
            const gunluk = k[aktifMetrik as keyof ZamanKullanici] as number[];
            let cum = 0;
            return {
              label: k.adSoyad,
              data: gunluk.map(v => (cum += v)),
              borderColor: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length],
              backgroundColor: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length] + '18',
              fill: true,
              tension: 0.4,
              pointRadius: 2,
              borderWidth: 2,
            };
          }),
        };

        // Karşılaştırma Bar grafiği — her metrik için personel toplamları
        const metrikKeys = Object.keys(METRIK_RENKLER) as (keyof typeof METRIK_RENKLER)[];
        const karsilastirmaData = {
          labels: aktifKullanicilar.map(k => k.adSoyad.split(' ')[0]), // sadece ad
          datasets: metrikKeys.map(mk => ({
            label: METRIK_RENKLER[mk].label,
            data: aktifKullanicilar.map(k => (k[mk as keyof ZamanKullanici] as number[]).reduce((a, b) => a + b, 0)),
            backgroundColor: METRIK_RENKLER[mk].bg.replace('0.15', '0.7'),
            borderColor: METRIK_RENKLER[mk].border,
            borderWidth: 1,
            borderRadius: 4,
          })),
        };

        // Radar/Yığılı bar — personel bazında toplam işlem
        const toplamIslemData = {
          labels: aktifKullanicilar.map(k => k.adSoyad),
          datasets: metrikKeys.map(mk => ({
            label: METRIK_RENKLER[mk].label,
            data: aktifKullanicilar.map(k => (k[mk as keyof ZamanKullanici] as number[]).reduce((a, b) => a + b, 0)),
            backgroundColor: METRIK_RENKLER[mk].bg.replace('0.15', '0.8'),
            borderColor: METRIK_RENKLER[mk].border,
            borderWidth: 1,
            borderRadius: 3,
          })),
        };

        const chartOpts: any = {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { position: 'top' as const, labels: { font: { size: 11 }, boxWidth: 12 } },
            tooltip: { mode: 'index' as const, intersect: false } },
          scales: {
            x: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 11 }, maxTicksLimit: 15 } },
            y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 11 }, stepSize: 1 }, beginAtZero: true },
          },
          interaction: { mode: 'index' as const, intersect: false },
        };

        const barOpts: any = {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { position: 'top' as const, labels: { font: { size: 11 }, boxWidth: 12 } } },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 12 } } },
            y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 11 }, stepSize: 1 }, beginAtZero: true },
          },
        };

        const yigiliBaOpts: any = {
          ...barOpts,
          scales: {
            ...barOpts.scales,
            x: { ...barOpts.scales.x, stacked: true },
            y: { ...barOpts.scales.y, stacked: true },
          },
          plugins: { ...barOpts.plugins, tooltip: { mode: 'index' as const } },
        };

        return (
          <div style={{ marginTop: 32 }}>
            {/* Zaman Çizelgesi */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('grafik.zamanCizelgesi', 'Zaman Çizelgesi')}</h3>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>{t('grafik.zamanCizelgesiDesc', 'Günlük işlem adetleri — personel bazlı')}</p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {/* Metrik seçici */}
                  {metrikKeys.map(mk => (
                    <button key={mk} onClick={() => setAktifMetrik(mk)}
                      style={{
                        padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1.5px solid',
                        background: aktifMetrik === mk ? METRIK_RENKLER[mk].border : '#fff',
                        color: aktifMetrik === mk ? '#fff' : METRIK_RENKLER[mk].border,
                        borderColor: METRIK_RENKLER[mk].border,
                      }}>
                      {METRIK_RENKLER[mk].label}
                    </button>
                  ))}
                </div>
                {/* Mod toggle */}
                <div style={{ display: 'flex', gap: 0, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  {(['gunluk', 'toplam'] as const).map(m => (
                    <button key={m} onClick={() => setGrafikMod(m)}
                      style={{ padding: '5px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
                        background: grafikMod === m ? '#1e293b' : '#fff', color: grafikMod === m ? '#fff' : '#64748b' }}>
                      {m === 'gunluk' ? t('grafik.gunluk', 'Günlük') : t('grafik.kumulatif', 'Kümülatif')}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ height: 300 }}>
                <Line data={grafikMod === 'gunluk' ? timelineData : kumulatifData} options={chartOpts} />
              </div>
            </div>

            {/* Karşılaştırma Grafikleri */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              {/* Grouped Bar — metrik × personel */}
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('grafik.metrikKarsilastirma', 'Metrik Karşılaştırması')}</h3>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>{t('grafik.metrikKarsilastirmaDesc', 'Personel başına dönem toplamları')}</p>
                <div style={{ height: 280 }}>
                  <Bar data={karsilastirmaData} options={barOpts} />
                </div>
              </div>

              {/* Stacked Bar — toplam işlem personel bazında */}
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('grafik.toplamIslemDagilimi', 'Toplam İşlem Dağılımı')}</h3>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>{t('grafik.toplamIslemDagilimiDesc', 'İşlem türlerine göre yığılı görünüm')}</p>
                <div style={{ height: 280 }}>
                  <Bar data={toplamIslemData} options={yigiliBaOpts} />
                </div>
              </div>
            </div>

            {/* Personel başına toplam işlem — yatay bar */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('grafik.personelSiralaması', 'Personel Sıralaması')}</h3>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>{t('grafik.personelSiralamasiDesc', 'Toplam işlem adedine göre sıralama')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  const siralama = aktifKullanicilar.map(k => ({
                    ad: k.adSoyad,
                    toplam: metrikKeys.reduce((sum, mk) => sum + (k[mk as keyof ZamanKullanici] as number[]).reduce((a, b) => a + b, 0), 0),
                    detay: metrikKeys.map(mk => ({
                      label: METRIK_RENKLER[mk].label,
                      color: METRIK_RENKLER[mk].border,
                      value: (k[mk as keyof ZamanKullanici] as number[]).reduce((a, b) => a + b, 0),
                    })),
                  })).sort((a, b) => b.toplam - a.toplam);

                  const maxToplam = Math.max(...siralama.map(s => s.toplam), 1);

                  return siralama.map((s, i) => (
                    <div key={s.ad} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length] + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: PERSONEL_RENKLER[i % PERSONEL_RENKLER.length], flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ width: 140, fontSize: 12, fontWeight: 600, color: '#374151', flexShrink: 0 }}>{s.ad}</div>
                      <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 999, height: 20, overflow: 'hidden', position: 'relative' }}>
                        {/* Segmentli bar */}
                        <div style={{ display: 'flex', height: '100%', width: `${(s.toplam / maxToplam) * 100}%` }}>
                          {s.detay.filter(d => d.value > 0).map(d => (
                            <div key={d.label} title={`${d.label}: ${d.value}`}
                              style={{ background: d.color, flex: d.value, transition: 'flex 0.3s' }} />
                          ))}
                        </div>
                      </div>
                      <div style={{ width: 36, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#1e293b', flexShrink: 0 }}>{s.toplam}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 240 }}>
                        {s.detay.filter(d => d.value > 0).map(d => (
                          <span key={d.label} style={{ fontSize: 10, background: d.color + '18', color: d.color, borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>
                            {d.label[0]}: {d.value}
                          </span>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PersonelPerformansRaporu;
